import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Loader2, Download, Banknote, 
  Users, DollarSign, Wallet, FileText, CheckCircle2,
  Pencil, Save, X
} from 'lucide-react';
import { payrollService } from '../../services/payrollService';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/StatusBadge';

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const emptyEditForm = () => ({ basic_salary: '', allowances_total: '', commission_amount: '', deductions_total: '' });

const PayrollDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canEdit = user && ['super_admin', 'hr_manager', 'finance_manager'].includes(user.role);

  const loadBatchData = async () => {
    try {
      const res = await payrollService.getBatch(id);
      setBatch(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBatchData(); }, [id]);

  const startEdit = (rec) => {
    setError('');
    setEditingId(rec.employee_id);
    setEditForm({
      basic_salary: String(rec.basic_salary),
      allowances_total: String(rec.allowances_total),
      commission_amount: String(rec.commission_amount),
      deductions_total: String(rec.deductions_total),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(emptyEditForm());
    setError('');
  };

  const saveEdit = async (employeeId) => {
    setSaving(true);
    setError('');
    try {
      const res = await payrollService.updateRecord(id, employeeId, {
        basic_salary: parseFloat(editForm.basic_salary) || 0,
        allowances_total: parseFloat(editForm.allowances_total) || 0,
        commission_amount: parseFloat(editForm.commission_amount) || 0,
        deductions_total: parseFloat(editForm.deductions_total) || 0,
      });
      setBatch(res.data);
      setEditingId(null);
      setEditForm(emptyEditForm());
    } catch (err) {
      setError(err.message || 'Failed to update record');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center p-20">
      <Loader2 className="animate-spin text-primary w-10 h-10 opacity-20" />
    </div>
  );

  if (!batch) return <div className="p-10 text-center">Batch not found.</div>;

  const isLocked = batch.status === 'Paid';

  return (
    <div className="space-y-6 pb-20 text-gray-900">
      <button 
        onClick={() => navigate('/payroll')} 
        className="flex items-center gap-2 text-gray-500 hover:text-primary transition-colors text-sm font-bold uppercase tracking-tighter"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Batches
      </button>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
          {error}
        </div>
      )}

      {/* Batch Summary Header */}
      <div className="bg-white rounded-card border border-gray-100 shadow-soft p-8 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-2 h-full bg-primary" />
        <div className="flex justify-between items-start flex-wrap gap-6">
          <div>
            <h1 className="text-3xl font-black tracking-tight">{MONTHS[batch.month - 1] + ' ' + batch.year} Payroll</h1>
            <p className="text-gray-400 font-bold uppercase text-[10px] tracking-[0.2em] mt-1">Batch ID: {batch.batch_id}</p>
            <div className="mt-4"><StatusBadge status={String(batch.status)} /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
            <SummaryItem label="Employees" value={batch.total_employees} icon={<Users />} />
            <SummaryItem label="Total Gross" value={batch.total_gross.toLocaleString() + ' PKR'} icon={<Wallet />} />
            <SummaryItem label="Total Net Pay" value={batch.total_net.toLocaleString() + ' PKR'} icon={<Banknote />} />
          </div>
        </div>
      </div>

      {/* Individual Records Table */}
      <div className="bg-white rounded-card border border-gray-100 shadow-soft overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/30 flex items-center justify-between">
          <h2 className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Employee Breakdown</h2>
          {isLocked && canEdit && (
            <span className="text-[10px] font-bold text-gray-400 uppercase">Locked — already paid</span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50/50 border-b border-gray-100">
              <tr className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                <th className="px-6 py-4">Employee</th>
                <th className="px-6 py-4">Basic Salary</th>
                <th className="px-6 py-4">Allowances</th>
                <th className="px-6 py-4">Commission</th>
                <th className="px-6 py-4">Deductions</th>
                <th className="px-6 py-4 text-right text-primary">Net Pay</th>
                {canEdit && !isLocked && <th className="px-6 py-4 text-right">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {batch.records.map((rec, i) => {
                const isEditing = editingId === rec.employee_id;
                return (
                  <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-bold text-gray-900">{rec.employee_name}</p>
                      <p className="text-[10px] text-gray-400 uppercase font-bold">{rec.department}</p>
                    </td>
                    {isEditing ? (
                      <>
                        <td className="px-6 py-4">
                          <input type="number" step="0.01" value={editForm.basic_salary}
                            onChange={(e) => setEditForm({ ...editForm, basic_salary: e.target.value })}
                            className="w-24 p-1.5 border rounded-lg text-sm bg-gray-50" />
                        </td>
                        <td className="px-6 py-4">
                          <input type="number" step="0.01" value={editForm.allowances_total}
                            onChange={(e) => setEditForm({ ...editForm, allowances_total: e.target.value })}
                            className="w-24 p-1.5 border rounded-lg text-sm bg-gray-50" />
                        </td>
                        <td className="px-6 py-4">
                          <input type="number" step="0.01" value={editForm.commission_amount}
                            onChange={(e) => setEditForm({ ...editForm, commission_amount: e.target.value })}
                            className="w-24 p-1.5 border rounded-lg text-sm bg-gray-50" />
                        </td>
                        <td className="px-6 py-4">
                          <input type="number" step="0.01" value={editForm.deductions_total}
                            onChange={(e) => setEditForm({ ...editForm, deductions_total: e.target.value })}
                            className="w-24 p-1.5 border rounded-lg text-sm bg-gray-50" />
                        </td>
                        <td className="px-6 py-4 text-right text-gray-400 text-xs italic">Recalculates on save</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => saveEdit(rec.employee_id)} disabled={saving}
                              className="p-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100" title="Save">
                              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            </button>
                            <button onClick={cancelEdit} disabled={saving}
                              className="p-1.5 bg-gray-50 text-gray-500 rounded-lg hover:bg-gray-100" title="Cancel">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-6 py-4 text-gray-600 font-medium">{rec.basic_salary.toLocaleString()}</td>
                        <td className="px-6 py-4 text-green-600 font-medium">+{rec.allowances_total.toLocaleString()}</td>
                        <td className="px-6 py-4 text-blue-600 font-medium">+{rec.commission_amount.toLocaleString()}</td>
                        <td className="px-6 py-4 text-red-500 font-medium">-{rec.deductions_total.toLocaleString()}</td>
                        <td className="px-6 py-4 text-right">
                          <span className="bg-primary/5 text-primary px-3 py-1.5 rounded-lg font-black">{rec.net_pay.toLocaleString()}</span>
                        </td>
                        {canEdit && !isLocked && (
                          <td className="px-6 py-4 text-right">
                            <button onClick={() => startEdit(rec)}
                              className="p-1.5 bg-gray-50 text-gray-500 rounded-lg hover:bg-gray-100" title="Edit">
                              <Pencil className="w-4 h-4" />
                            </button>
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Sub-component for Header Summary
const SummaryItem = ({ label, value, icon }) => (
  <div>
    <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest flex items-center gap-1.5">
      {React.cloneElement(icon, { className: "w-3 h-3" })} {label}
    </p>
    <p className="text-lg font-black text-gray-800 mt-1">{value}</p>
  </div>
);

export default PayrollDetail;